/* Der Pia-Thread (30.10.2026): EIN Verlauf je Nutzer, gezeigt auf /pia UND im Panel auf /heute (gleiche Komponente, gleiche Daten).
   Lädt neu alle 15 s, markiert Pias Nachrichten als gelesen, solange der Verlauf sichtbar ist. Karten: PiaKarte (generisch). */
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Mic, Send, Square, Volume2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import PiaKarte from '@/components/PiaKarte';
import { AufnahmeLeiste, MikrofonKnopf, direktSendenLesen, direktSendenSchreiben, useSprachaufnahme } from '@/components/PiaMikrofon';
import * as stimme from '@/lib/piaStimme';
import { piaStream } from '@/lib/piaStream';

const fehler = (e, std) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : std);
const uhr = (iso) => { try { return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }); } catch (e) { return ''; } };
const tag = (iso) => { try { return new Date(iso).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' }); } catch (e) { return ''; } };

/* Lautsprecher an Pias Nachricht: vorlesen bzw. stoppen (Stimme des Geräts, lib/piaStimme). */
function VorlesenKnopf({ nachricht, sprichtId }) {
  if (!stimme.verfuegbar()) return null;
  const text = nachricht.text || nachricht.karte?.titel || '';
  if (!text.trim()) return null;
  const laeuft = sprichtId === nachricht.id;
  return (
    <button type="button" onClick={() => (laeuft ? stimme.stoppen() : stimme.vorlesen(text, nachricht.id))} data-testid="pia-vorlesen"
      aria-label={laeuft ? 'Vorlesen stoppen' : 'Nachricht vorlesen'} title={laeuft ? 'Stopp' : 'Vorlesen'}
      className="inline-flex items-center justify-center w-8 h-8 -my-1.5 -mr-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-background/60 focus:outline-none focus:ring-2 focus:ring-marke">
      {laeuft ? <Square className="w-3.5 h-3.5" aria-hidden="true" /> : <Volume2 className="w-4 h-4" aria-hidden="true" />}
    </button>
  );
}

/* Was Pia gerade nachschlägt (Ereignis `werkzeug` im Strom), statt „denkt nach“. */
const NACHSCHLAGEN = {
  termine: 'Ich schaue in den Kalender …', lead_dossier: 'Ich lese die Akte …', pipeline: 'Ich schaue in die Pipeline …',
  kpis: 'Ich rechne die Zahlen …', aufgaben: 'Ich schaue in die Aufgaben …', kursauslastung: 'Ich schaue in den Kursplan …',
  abwesende_mitglieder: 'Ich suche, wer länger nicht da war …', ziele: 'Ich schaue auf die Ziele …', rueckmeldungen: 'Ich lese die Rückmeldungen …',
  lecks_bericht: 'Ich prüfe, wo Umsatz liegen bleibt …', offene_posten: 'Ich schaue in die offenen Posten …',
  freie_probetrainings: 'Ich suche freie Probetrainings …', abrechnung_vormerken: 'Ich bereite die Abrechnung vor …',
};
const nachschlagen = (name) => NACHSCHLAGEN[name] || 'Ich bereite das vor …';

/* Satz, den Pia statt einer Karte vorliest: Bestätigungskarten werden NIE per Sprache ausgeführt, nur per Klick. */
export const KARTE_VORBEREITET = 'Ich habe es vorbereitet, bitte bestätige.';

/* kontext: {seite, lead_id} – die Seite, auf der Pia gerade offen ist (Seitenfenster); Pia bezieht „ihr“/„sie“ dann auf den offenen Lead.
   gespraech: Gesprächsmodus (Home-Bildschirm-App und Seitenfenster): großer Mikrofon-Knopf (halten oder antippen), erkannter Text geht direkt raus. */
export default function PiaThread({ darfFragen = true, hoehe = 'h-[60vh]', onGelesen, kontext = null, gespraech = false }) {
  const [n, setN] = useState(null);
  const [mehr, setMehr] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [korrekturen, setKorrekturen] = useState([]);
  const [live, setLive] = useState(null);       // laufende Antwort: {frage, text, status} — verschwindet, sobald sie gespeichert ist
  const origRef = useRef('');
  const textRef = useRef('');
  textRef.current = text;
  const unten = useRef(null);
  const box = useRef(null);
  const nachUnten = useRef(true);
  const eingabe = useRef(null);
  const [direkt, setDirekt] = useState(direktSendenLesen);
  const direktRef = useRef(direkt);
  const sprichtId = stimme.useSprichtGerade();
  const geladen = useRef(false);   // erster Abruf durch? (kein `n` in den Abhängigkeiten von `lade` — sonst lädt jeder Abruf den nächsten)

  const lade = useCallback(async () => {
    try {
      const { data } = await api.get('/assistenz/thread', { params: { limit: 50 } });
      setN((alt) => {
        const aeltere = (alt || []).filter((x) => data.nachrichten.length && x.erstellt_am < data.nachrichten[0].erstellt_am);
        return [...aeltere, ...data.nachrichten];
      });
      setMehr((m) => (geladen.current ? m : data.mehr));
      geladen.current = true;
      if (data.nachrichten.some((x) => x.von === 'pia' && !x.gelesen) && document.visibilityState === 'visible') {
        await api.post('/assistenz/thread/gelesen'); onGelesen?.();
      }
    } catch (e) { if (!geladen.current) setN([]); }
  }, [onGelesen]);

  useEffect(() => { lade(); const t = setInterval(lade, 15000); return () => clearInterval(t); }, [lade]);
  useEffect(() => { if (nachUnten.current) unten.current?.scrollIntoView({ block: 'end' }); }, [n, live]);

  const aelteres = async () => {
    if (!n?.length) return;
    try {
      const { data } = await api.get('/assistenz/thread', { params: { limit: 50, vor: n[0].erstellt_am } });
      nachUnten.current = false;
      setN([...data.nachrichten, ...n]); setMehr(data.mehr);
    } catch (e) { toast.error(fehler(e, 'Nicht geladen')); }
  };

  /* Fragen über den Strom: die Frage steht sofort da, die Antwort wächst Stück für Stück, das Gerät liest Satz für Satz vor. Fehlt die
     Strom-Route (älterer Server während eines Deploys), geht es über POST /thread wie bisher. */
  const sendeText = useCallback(async (roh, gesprochen = false) => {
    const t = (roh || '').trim();
    if (!t) return;
    stimme.stoppen();
    setBusy(true);
    const orig = origRef.current;                          // erkannter Text, den du vor dem Senden geändert hast (lehrt das Wörterbuch)
    const m = stimme.modus();                              // vorlesen: „immer“ oder „nach Spracheingabe“ (nur, wenn gesprochen wurde)
    const vorlesen = stimme.verfuegbar() && (m === 'immer' || (m === 'sprache' && (gesprochen || !!orig)));
    const k = typeof kontext === 'function' ? kontext() : kontext;
    const body = { text: t, ...(orig && orig.trim() !== t ? { sprache_original: orig } : {}), ...(k ? { kontext: k } : {}),
      ...(vorlesen ? { gesprochen: true } : {}) };
    const fertig = (data) => {
      origRef.current = ''; setKorrekturen([]);
      nachUnten.current = true;
      const neu = [data.nachricht, ...(data.antwort ? [data.antwort] : [])];
      const ids = new Set(neu.map((x) => x.id));
      setN((alt) => [...(alt || []).filter((x) => !ids.has(x.id)), ...neu]);      // der 15-s-Abruf kann die Frage schon geholt haben
      setText('');
    };
    let sp = null;
    let vorbereitet = false;
    let gestreamt = '';
    nachUnten.current = true;
    setLive({ frage: t, text: '', status: null });
    try {
      const data = await piaStream(body, (e) => {
        if (e.typ === 'werkzeug') setLive((l) => (l ? { ...l, status: nachschlagen(e.name) } : l));
        else if (e.typ === 'vorbereitet') { vorbereitet = true; sp?.abbrechen(); sp = null; }
        else if (e.typ === 'text') {
          gestreamt += e.text;
          setLive((l) => (l ? { ...l, text: l.text + e.text, status: null } : l));
          if (vorlesen && !vorbereitet) { if (!sp) sp = stimme.sprecher(); sp?.dazu(e.text); }
        }
      });
      setLive(null);
      fertig(data);
      const a = data.antwort;
      if (vorlesen && a) {
        if (vorbereitet || a.karte?.daten?.aktion_id) { sp?.abbrechen(); stimme.vorlesen(KARTE_VORBEREITET, a.id); }       // Bestätigungskarte: nur der feste Satz
        else if (sp) { sp.heisst(a.id); sp.ende(); }
        else if (!gestreamt && a.text) stimme.vorlesen(a.text, a.id);               // ohne Strom-Text (Dauerauftrag, ausgeschaltet)
      }
    } catch (err) {
      sp?.abbrechen();
      setLive(null);
      if (err.status === 404 || err.status === 405) {           // Strom-Route fehlt: der bisherige Weg
        try {
          const { data } = await api.post('/assistenz/thread', body);
          fertig(data);
          const a = data.antwort;
          const lesen = a?.karte?.daten?.aktion_id ? KARTE_VORBEREITET : a?.text;
          if (vorlesen && lesen) stimme.vorlesen(lesen, a.id);
        } catch (e2) { toast.error(fehler(e2, 'Nicht gesendet')); }
      } else {
        toast.error(err.detail || (err.abgebrochen ? 'Verbindung abgebrochen, die Antwort steht gleich im Verlauf' : 'Nicht gesendet'));
        lade();                                                // die Frage ist evtl. schon gespeichert und die Antwort folgt im Verlauf
      }
    }
    setBusy(false);
  }, [kontext, lade]);

  const senden = (e) => { e.preventDefault(); stimme.entsperren(); if (!busy) sendeText(text); };

  /* Spracheingabe: Text ans Eingabefeld anhängen und fokussieren (korrigierbar); mit dem Schalter direkt senden. */
  const sprache = useSprachaufnahme({
    onText: (erkannt, modell, daten) => {
      setKorrekturen(daten?.korrekturen || []);
      if (direktRef.current || gespraech) { sendeText(erkannt, true); return; }
      const basis = textRef.current.trim();
      const neu = (basis ? `${basis} ${erkannt}` : erkannt).slice(0, 1000);
      origRef.current = neu;
      setText(neu);
      setTimeout(() => eingabe.current?.focus(), 0);
    },
  });
  const spricht = sprache.zustand !== 'ruhig';
  const aufnahme = { ...sprache, fertig: () => { stimme.entsperren(); sprache.fertig(); } };   // Tippen auf „Fertig“ gibt am iPhone die Stimme frei
  const mikroStart = () => { stimme.stoppen(); stimme.entsperren(); sprache.start(); };
  const direktUmschalten = (an) => { setDirekt(an); direktRef.current = an; direktSendenSchreiben(an); };
  const [liest, setLiest] = useState(() => stimme.modus() !== 'aus');
  const vorlesenUmschalten = (an) => { setLiest(an); stimme.modusSetzen(an ? 'sprache' : 'aus'); if (!an) stimme.stoppen(); };
  /* Halten und loslassen = senden; kurzes Antippen = Aufnahme starten, erneutes Antippen = fertig. */
  const gedrueckt = useRef(0);
  const mikroRunter = (e) => {
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (x) { /* egal */ }
    if (sprache.zustand === 'aufnahme') { gedrueckt.current = 0; aufnahme.fertig(); return; }
    if (sprache.zustand !== 'ruhig' || busy) return;
    gedrueckt.current = Date.now(); mikroStart();
  };
  const mikroHoch = () => {
    const lang = gedrueckt.current && Date.now() - gedrueckt.current > 700;
    gedrueckt.current = 0;
    if (lang && sprache.zustand === 'aufnahme') aufnahme.fertig();
  };

  const neuesThema = async () => {
    try { await api.post('/assistenz/thread/neues-thema'); nachUnten.current = true; await lade(); toast.success('Neues Thema: Pia fängt frisch an'); }
    catch (e) { toast.error(fehler(e, 'Ging gerade nicht')); }
  };
  const neuesThemaKnopf = (
    <button type="button" onClick={neuesThema} disabled={busy} data-testid="pia-neues-thema"
      className="text-xs font-medium text-muted-foreground hover:text-foreground underline-offset-2 hover:underline min-h-[28px] disabled:opacity-50">
      Neues Thema
    </button>
  );

  if (n === null) return <div className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  let letzterTag = '';
  return (
    <div className="flex flex-col min-h-0" data-testid="pia-thread">
      <div ref={box} className={`${hoehe} overflow-y-auto space-y-2 pr-1`} onScroll={() => { const b = box.current; nachUnten.current = b.scrollHeight - b.scrollTop - b.clientHeight < 80; }}>
        {mehr && <div className="text-center"><Button size="sm" variant="ghost" onClick={aelteres}>Ältere Nachrichten</Button></div>}
        {n.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">Noch keine Nachrichten. Pia meldet sich hier, sobald es etwas gibt.</p>}
        {n.map((x) => {
          const t = tag(x.erstellt_am); const neuerTag = t !== letzterTag; letzterTag = t;
          const pia = x.von === 'pia';
          if (x.karte?.art === 'neues_thema') {     // 03.11.2026: Trenner — Pia liest nur, was danach kommt
            return (
              <div key={x.id}>
                {neuerTag && <div className="text-center text-[11px] text-muted-foreground py-1">{t}</div>}
                <div className="flex items-center gap-2 text-[11px] text-muted-foreground py-1.5" data-testid="pia-neues-thema-trenner">
                  <span className="flex-1 border-t border-border" />Neues Thema · {uhr(x.erstellt_am)}<span className="flex-1 border-t border-border" />
                </div>
              </div>
            );
          }
          return (
            <div key={x.id}>
              {neuerTag && <div className="text-center text-[11px] text-muted-foreground py-1">{t}</div>}
              <div className={`flex ${pia ? 'justify-start' : 'justify-end'}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${pia ? 'bg-muted' : 'bg-marke text-white'}`} data-testid={pia ? 'pia-nachricht' : 'nutzer-nachricht'}>
                  <div className="whitespace-pre-wrap break-words">{x.text}</div>
                  {pia && <PiaKarte karte={x.karte} nachrichtId={x.id} onAktion={lade} />}
                  <div className={`flex items-center justify-between gap-2 text-[10px] mt-1 ${pia ? 'text-muted-foreground' : 'text-white/80'}`}>
                    <span>{uhr(x.erstellt_am)}</span>
                    {pia && <VorlesenKnopf nachricht={x} sprichtId={sprichtId} />}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        {live && (
          <div data-testid="pia-live">
            <div className="flex justify-end">
              <div className="max-w-[85%] rounded-2xl px-3 py-2 text-sm bg-marke text-white"><div className="whitespace-pre-wrap break-words">{live.frage}</div></div>
            </div>
            <div className="flex justify-start mt-2">
              <div className="max-w-[85%] rounded-2xl px-3 py-2 text-sm bg-muted" aria-live="polite">
                {live.text ? <div className="whitespace-pre-wrap break-words">{live.text}<span className="inline-block w-1.5 h-3.5 ml-0.5 align-[-2px] bg-foreground/40 animate-pulse" aria-hidden="true" /></div>
                  : <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />{live.status || 'Pia denkt nach …'}</div>}
              </div>
            </div>
          </div>
        )}
        <div ref={unten} />
      </div>
      {darfFragen && gespraech ? (
        <div className="pt-3 space-y-2" data-testid="pia-gespraech">
          {spricht && <AufnahmeLeiste s={aufnahme} />}
          <div className="flex flex-col items-center gap-1.5">
            <button type="button" onPointerDown={mikroRunter} onPointerUp={mikroHoch} onPointerCancel={mikroHoch} onContextMenu={(e) => e.preventDefault()}
              disabled={busy && !spricht} data-testid="pia-gespraech-mikro" aria-label="Sprechen: halten oder antippen"
              className={`select-none touch-none inline-flex items-center justify-center w-20 h-20 rounded-full text-white shadow-lg focus:outline-none focus:ring-4 focus:ring-marke/40 disabled:opacity-50 ${sprache.zustand === 'aufnahme' ? 'bg-destructive animate-pulse' : 'bg-marke'}`}>
              {busy || sprache.zustand === 'erkennen' ? <Loader2 className="w-8 h-8 animate-spin" aria-hidden="true" /> : <Mic className="w-8 h-8" aria-hidden="true" />}
            </button>
            <p className="text-xs text-muted-foreground" aria-live="polite">
              {busy ? (live?.status || (live?.text ? 'Pia antwortet …' : 'Pia denkt nach …')) : sprache.zustand === 'aufnahme' ? 'Loslassen oder antippen zum Senden' : sprache.zustand === 'erkennen' ? 'Wird erkannt …' : 'Halten oder antippen und sprechen'}
            </p>
          </div>
          {sprache.fehler && <p role="alert" className="text-xs text-destructive" data-testid="pia-sprache-fehler">{sprache.fehler}</p>}
          <form onSubmit={senden} className="flex gap-2">
            <input ref={eingabe} value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} placeholder="oder tippen …" data-testid="pia-eingabe"
              className="flex-1 min-w-0 h-11 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-marke" />
            <Button type="submit" disabled={busy || !text.trim()} className="min-h-[44px]" data-testid="pia-senden" aria-label="Senden"><Send className="w-4 h-4" /></Button>
          </form>
          <div className="flex items-center justify-between gap-2">
            {stimme.verfuegbar() ? (
              <label className="flex items-center gap-2 text-xs text-muted-foreground min-h-[28px] cursor-pointer">
                <input type="checkbox" checked={liest} onChange={(e) => vorlesenUmschalten(e.target.checked)} data-testid="pia-vorlesen-schalter" />
                Pia liest Antworten vor
              </label>
            ) : <span />}
            {neuesThemaKnopf}
          </div>
        </div>
      ) : darfFragen ? (
        <div className="pt-3 space-y-1.5">
          <form onSubmit={senden} className="flex gap-2">
            {spricht ? <AufnahmeLeiste s={aufnahme} /> : (
              <>
                <input ref={eingabe} value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} placeholder="Frag Pia …" data-testid="pia-eingabe"
                  className="flex-1 min-w-0 h-11 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-marke" />
                <MikrofonKnopf onClick={mikroStart} disabled={busy} />
                <Button type="submit" disabled={busy || !text.trim()} className="gap-1.5 min-h-[44px]" data-testid="pia-senden">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}<span className="hidden sm:inline">Senden</span></Button>
              </>
            )}
          </form>
          {korrekturen.length > 0 && !spricht && (
            <p className="text-[11px] text-muted-foreground" data-testid="pia-korrekturen">korrigiert: {korrekturen.map((k) => `${k.von} → ${k.zu}`).join(', ')}</p>
          )}
          {sprache.fehler && <p role="alert" className="text-xs text-destructive" data-testid="pia-sprache-fehler">{sprache.fehler}</p>}
          <div className="flex items-center justify-between gap-2">
            <label className="flex items-center gap-2 text-xs text-muted-foreground min-h-[28px] cursor-pointer">
              <input type="checkbox" checked={direkt} onChange={(e) => direktUmschalten(e.target.checked)} data-testid="pia-direkt-senden" />
              Nach dem Sprechen direkt senden
            </label>
            {neuesThemaKnopf}
          </div>
        </div>
      ) : <p className="text-xs text-muted-foreground pt-3">Hier kommen deine Briefings und Nachbereitungen. Die Knöpfe auf den Karten kannst du nutzen; fragen kannst du Pia nicht.</p>}
    </div>
  );
}
